import React, { useState, useRef } from "react";
import { toast } from "react-toastify";

const VideoReactions = ({ freelancer, onLike }) => {
  const [showPopup, setShowPopup] = useState(false);
  const hoverTimeout = useRef(null);

  const handleMouseEnter = () => {
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    hoverTimeout.current = setTimeout(() => {
      setShowPopup(true);
    }, 400); // 400ms delay to prevent accidental popups
  };

  const handleMouseLeave = () => {
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    hoverTimeout.current = setTimeout(() => {
      setShowPopup(false);
    }, 300);
  };

  const handleReactionClick = (type) => {
    onLike(freelancer.id, type);
    setShowPopup(false);
  };

  const handleLikeClick = () => {
    const activeReaction = Object.keys(freelancer.user_liked || {}).find(
      (key) => freelancer.user_liked[key]
    );

    if (activeReaction) {
      onLike(freelancer.id, activeReaction);
    } else {
      onLike(freelancer.id, "like");
    }
  };

  const [copied, setCopied] = useState(false);
  const shareUrl = `${window.location.origin}/meet-freelancers?video=${freelancer.id}`;

  const handleShareClick = (e) => {
    e.stopPropagation(); // Stop propagation to prevent card modal from opening
    
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(shareUrl)
        .then(() => {
          setCopied(true);
          toast.success("Video share link copied!");
          setTimeout(() => setCopied(false), 2000);
        })
        .catch(() => {
          fallbackCopy(shareUrl);
        });
    } else {
      fallbackCopy(shareUrl);
    }
  };

  const fallbackCopy = (text) => {
    try {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const successful = document.execCommand("copy");
      document.body.removeChild(textArea);
      if (successful) {
        setCopied(true);
        toast.success("Video share link copied!");
        setTimeout(() => setCopied(false), 2000);
      } else {
        alert("Failed to copy link. Please manually copy: " + text);
      }
    } catch (err) {
      alert("Failed to copy link. Please manually copy: " + text);
    }
  };

  // Get active reactions list
  const activeReactionTypes = freelancer.user_liked || {};
  const currentActiveReaction = Object.keys(activeReactionTypes).find(key => activeReactionTypes[key]);

  // Calculate total likes count
  const totalLikes = (freelancer.likes?.like || 0) + 
                     (freelancer.likes?.heart || 0) + 
                     (freelancer.likes?.care || 0) + 
                     (freelancer.likes?.thumb || 0);

  // Determine standard Like Button label and icon/emoji
  let likeBtnLabel = "Like";
  let likeBtnElement = <i className="bi bi-hand-thumbs-up" style={{ fontSize: "0.95rem" }}></i>;
  let likeBtnColor = "#cbd5e1";

  if (currentActiveReaction === "like") {
    likeBtnLabel = "Like";
    likeBtnElement = <span style={{ fontSize: "1.05rem", lineHeight: 1 }}>👍</span>;
    likeBtnColor = "#10b981"; // green
  } else if (currentActiveReaction === "heart") {
    likeBtnLabel = "Love";
    likeBtnElement = <span style={{ fontSize: "1.05rem", lineHeight: 1 }}>❤️</span>;
    likeBtnColor = "#ef4444"; // red
  } else if (currentActiveReaction === "care") {
    likeBtnLabel = "Care";
    likeBtnElement = <span style={{ fontSize: "1.05rem", lineHeight: 1 }}>🥰</span>;
    likeBtnColor = "#f59e0b"; // orange/yellow
  } else if (currentActiveReaction === "thumb") {
    likeBtnLabel = "Support";
    likeBtnElement = <span style={{ fontSize: "1.05rem", lineHeight: 1 }}>👏</span>;
    likeBtnColor = "#3b82f6"; // blue
  }

  // Reactions data
  const reactions = [
    { type: "like", emoji: "👍", label: "Like" },
    { type: "heart", emoji: "❤️", label: "Love" },
    { type: "care", emoji: "🥰", label: "Care" },
    { type: "thumb", emoji: "👏", label: "Support" },
  ];

  // Get active emojis based on reaction counts
  const activeReactionEmojis = [];
  if (freelancer.likes?.like > 0) activeReactionEmojis.push("👍");
  if (freelancer.likes?.heart > 0) activeReactionEmojis.push("❤️");
  if (freelancer.likes?.care > 0) activeReactionEmojis.push("🥰");
  if (freelancer.likes?.thumb > 0) activeReactionEmojis.push("👏");
  if (activeReactionEmojis.length === 0 && totalLikes > 0) {
    activeReactionEmojis.push("👍");
  }

  return (
    <div style={{ width: "100%", marginTop: "8px", borderTop: "1px solid rgba(255, 255, 255, 0.06)", paddingTop: "8px" }}>
      {/* Stats Summary Bar with Stacked Emojis */}
      {totalLikes > 0 && (
        <div style={{ display: "flex", alignItems: "center", marginBottom: "8px", padding: "0 2px" }}>
          <div style={{ display: "flex", alignItems: "center" }}>
            {activeReactionEmojis.slice(0, 3).map((emoji, idx) => (
              <span 
                key={idx} 
                style={{ 
                  fontSize: "1rem", 
                  marginLeft: idx > 0 ? "-4px" : "0", 
                  zIndex: 10 - idx,
                  lineHeight: 1,
                  display: "inline-block"
                }}
              >
                {emoji}
              </span>
            ))}
            <span style={{ marginLeft: "6px", fontWeight: 600, color: "#cbd5e1", fontSize: "0.75rem" }}>{totalLikes}</span>
          </div>
        </div>
      )}

      {/* Action Buttons Row */}
      <div style={{ display: "flex", gap: "6px", padding: "2px 0" }}>
        {/* Hover Container for Like Reaction */}
        <div 
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          style={{ flex: 1, position: "relative" }}
        >
          {/* Facebook Style Floating Reactions Popup */}
          {showPopup && (
            <div style={{
              position: "absolute",
              bottom: "100%",
              left: "0",
              background: "#1e293b",
              boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
              borderRadius: "30px",
              padding: "6px 12px",
              display: "flex",
              gap: "10px",
              zIndex: 100,
              transform: "translateY(-8px)",
              animation: "popupFadeIn 0.2s cubic-bezier(0.18, 0.89, 0.32, 1.28) forwards",
              border: "1px solid rgba(255,255,255,0.08)"
            }}>
              {reactions.map((r) => (
                <button
                  key={r.type}
                  onClick={() => handleReactionClick(r.type)}
                  title={r.label}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: "1.5rem",
                    cursor: "pointer",
                    transition: "transform 0.15s ease",
                    padding: 0,
                    margin: 0,
                    outline: "none",
                  }}
                  onMouseOver={(e) => e.target.style.transform = "scale(1.3) translateY(-4px)"}
                  onMouseOut={(e) => e.target.style.transform = "scale(1) translateY(0)"}
                >
                  {r.emoji}
                </button>
              ))}
            </div>
          )}

          <button
            onClick={handleLikeClick}
            style={{
              width: "100%",
              background: "rgba(255,255,255,0.02)",
              border: "1px solid rgba(255,255,255,0.05)",
              padding: "5px 0",
              color: likeBtnColor === "rgba(128, 128, 128, 0.6)" ? "#94a3b8" : likeBtnColor,
              fontWeight: 600,
              fontSize: "0.75rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "4px",
              cursor: "pointer",
              borderRadius: "8px",
              transition: "all 0.2s"
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = "rgba(255,255,255,0.05)";
              e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = "rgba(255,255,255,0.02)";
              e.currentTarget.style.borderColor = "rgba(255,255,255,0.05)";
            }}
          >
            {likeBtnElement}
            <span>{likeBtnLabel} {totalLikes > 0 ? `(${totalLikes})` : ''}</span>
          </button>
        </div>

        {/* Visit Profile Button */}
        <button
          onClick={() => window.open(`/profileOtherPerson/${freelancer.user_name}`, "_blank")}
          style={{
            flex: 1,
            background: "rgba(255,255,255,0.02)",
            border: "1px solid rgba(255,255,255,0.05)",
            padding: "5px 0",
            color: "#cbd5e1",
            fontWeight: 600,
            fontSize: "0.75rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "4px",
            cursor: "pointer",
            borderRadius: "8px",
            transition: "all 0.2s"
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.background = "rgba(255,255,255,0.05)";
            e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)";
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.background = "rgba(255,255,255,0.02)";
            e.currentTarget.style.borderColor = "rgba(255,255,255,0.05)";
          }}
        >
          <i className="bi bi-person-circle" style={{ fontSize: "0.85rem" }}></i>
          <span>Profile</span>
        </button>

        {/* Share Button (Copy link) */}
        <button
          onClick={handleShareClick}
          style={{
            flex: 1,
            background: copied ? "rgba(16, 185, 129, 0.1)" : "rgba(255,255,255,0.02)",
            border: copied ? "1px solid rgba(16, 185, 129, 0.4)" : "1px solid rgba(255,255,255,0.05)",
            padding: "5px 0",
            color: copied ? "#10b981" : "#94a3b8",
            fontWeight: 600,
            fontSize: "0.75rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "4px",
            cursor: "pointer",
            borderRadius: "8px",
            transition: "all 0.2s"
          }}
          onMouseOver={(e) => {
            if (!copied) {
              e.currentTarget.style.background = "rgba(255,255,255,0.05)";
              e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)";
            }
          }}
          onMouseOut={(e) => {
            if (!copied) {
              e.currentTarget.style.background = "rgba(255,255,255,0.02)";
              e.currentTarget.style.borderColor = "rgba(255,255,255,0.05)";
            }
          }}
        >
          <i className={copied ? "bi bi-check-circle-fill" : "bi bi-share-fill"} style={{ fontSize: "0.9rem" }}></i>
          <span>{copied ? "Copied!" : "Share"}</span>
        </button>
      </div>

      {/* Keyframe Animation */}
      <style>{`
        @keyframes popupFadeIn {
          from {
            opacity: 0;
            transform: translateY(0) scale(0.8);
          }
          to {
            opacity: 1;
            transform: translateY(-8px) scale(1);
          }
        }
      `}</style>
    </div>
  );
};

export default VideoReactions;
